import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { DeferredCommitStatusGlyph } from "@/components/mrp/DeferredCommitStatusGlyph";
import { apiFetch } from "@/lib/api";
import { isIntegratorAdmin, meQueryKey, useIntegratorMe } from "@/lib/me-query";
import { APPS, type AppDescriptor } from "@/components/AppSwitcher";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { hubStickyTableHeadClassName, hubTableScrollRegionClassName } from "@/lib/hubLayoutClassNames";
import { CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintContent, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Navigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

type PivotView = "role" | "module";

type RolePivotSortKey = "module" | "path" | "access";
type ModulePivotSortKey = "role" | "access";

const ACCESS_SELECT_CLASS =
  "w-full max-w-[14rem] rounded-md border border-slate-300 bg-white py-1.5 pl-2 pr-7 text-sm text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

type HubModuleAccessLevel = "read" | "read_write" | "admin";

interface SubappRow {
  roleId: number;
  name: string | null;
  subapps: string[];
  moduleAccess?: { key: string; access: HubModuleAccessLevel }[];
  rawRowCount: number;
}

interface SubappAssignmentsResponse {
  scopeOrgId: number;
  allowedSubappKeys: string[];
  rows: SubappRow[];
}

function roleName(row: SubappRow): string {
  const trimmed = row.name?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : `Role ${row.roleId}`;
}

function moduleAccessMapFromRow(row: SubappRow): Map<string, HubModuleAccessLevel> {
  const map = new Map<string, HubModuleAccessLevel>();
  if (row.moduleAccess && row.moduleAccess.length > 0) {
    for (const entry of row.moduleAccess) {
      map.set(entry.key, entry.access);
    }
    return map;
  }
  for (const key of row.subapps) {
    map.set(key, "read_write");
  }
  return map;
}

function moduleMapsEqual(
  saved: Map<string, HubModuleAccessLevel>,
  draft: Map<string, HubModuleAccessLevel>,
): boolean {
  if (saved.size !== draft.size) {
    return false;
  }
  for (const [key, access] of saved) {
    if (draft.get(key) !== access) {
      return false;
    }
  }
  return true;
}

function accessSelectValueRank(value: "off" | HubModuleAccessLevel): number {
  switch (value) {
    case "off":
      return 0;
    case "read":
      return 1;
    case "read_write":
      return 2;
    case "admin":
      return 3;
    default:
      return 0;
  }
}

function compareStrings(left: string, right: string, dir: SortDirection): number {
  const result = left.localeCompare(right, undefined, { sensitivity: "base" });
  return dir === "asc" ? result : -result;
}

function modulePermissionsPageHint(scopeOrgId: number): string {
  return `Choose which integrator modules each SGS role may open, and whether each is read-only, read plus operational write, or module administrator. Use By role to edit one role across all modules, or By module to compare roles for a single module. This applies to organisation id ${scopeOrgId} (Smart Grid Solutions). The integrator administrator role has every module at the administrator tier and is omitted here.`;
}

function columnsForResponse(allowedKeys: string[], fallbackRows: SubappRow[]): AppDescriptor[] {
  const firstNonAdmin = fallbackRows.find((row) => !isIntegratorAdmin(row.roleId));
  const keysFromRow = firstNonAdmin ? [...moduleAccessMapFromRow(firstNonAdmin).keys()] : [];
  const set = new Set(allowedKeys.length > 0 ? allowedKeys : keysFromRow);
  if (set.size === 0) {
    return [...APPS];
  }
  return APPS.filter((app) => set.has(app.key));
}

/**
 * SGS: assign which integrator modules each `user_role` may open, and whether each is read-only, read plus
 * operational write, or module administrator (includes hub settings for that module). Integrator admin always has
 * every module at the administrator tier and is omitted from the matrix.
 */
export function SubappAccessPage() {
  const queryClient = useQueryClient();
  const { data: me } = useIntegratorMe();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["user-admin", "subapp-assignments"],
    queryFn: () => apiFetch<SubappAssignmentsResponse>("/api/user-admin/subapp-assignments"),
  });

  const [draft, setDraft] = useState<Map<number, Map<string, HubModuleAccessLevel>> | null>(null);
  const [pivotView, setPivotView] = useState<PivotView>("role");
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");
  const [selectedModuleKey, setSelectedModuleKey] = useState<string>("");
  const [newRoleName, setNewRoleName] = useState("");
  const [showAddRole, setShowAddRole] = useState(false);
  const [rolePivotSortKey, setRolePivotSortKey] = useState<RolePivotSortKey>("module");
  const [rolePivotSortDir, setRolePivotSortDir] = useState<SortDirection>("asc");
  const [modulePivotSortKey, setModulePivotSortKey] = useState<ModulePivotSortKey>("role");
  const [modulePivotSortDir, setModulePivotSortDir] = useState<SortDirection>("asc");

  const tableRows = useMemo(
    () => (data?.rows ?? []).filter((row) => !isIntegratorAdmin(row.roleId)),
    [data?.rows],
  );

  const moduleRows = useMemo(() => {
    const list = columnsForResponse(data?.allowedSubappKeys ?? [], data?.rows ?? []);
    return [...list].sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: "base" }),
    );
  }, [data?.allowedSubappKeys, data?.rows]);

  useEffect(() => {
    if (selectedRoleId !== "" && tableRows.some((row) => row.roleId === selectedRoleId)) {
      return;
    }
    setSelectedRoleId(tableRows[0]?.roleId ?? "");
  }, [tableRows, selectedRoleId]);

  useEffect(() => {
    if (selectedModuleKey && moduleRows.some((mod) => mod.key === selectedModuleKey)) {
      return;
    }
    setSelectedModuleKey(moduleRows[0]?.key ?? "");
  }, [moduleRows, selectedModuleKey]);

  const addRoleMutation = useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ role: { id: number; name: string | null } }>("/api/user-admin/roles", {
        method: "POST",
        body: { name },
      }),
    onSuccess: async (payload) => {
      setNewRoleName("");
      setShowAddRole(false);
      setDraft((previous) => {
        if (!previous) {
          return previous;
        }
        const next = new Map(previous);
        next.set(payload.role.id, new Map<string, HubModuleAccessLevel>());
        return next;
      });
      setSelectedRoleId(payload.role.id);
      await queryClient.invalidateQueries({ queryKey: ["user-admin", "subapp-assignments"] });
      await queryClient.invalidateQueries({ queryKey: ["user-admin", "roles"] });
    },
  });

  const submitNewRole = () => {
    const name = newRoleName.trim();
    if (!name || !hubWritesAllowed || addRoleMutation.isPending) {
      return;
    }
    addRoleMutation.mutate(name);
  };

  useEffect(() => {
    if (!data?.rows) {
      return;
    }
    const next = new Map<number, Map<string, HubModuleAccessLevel>>();
    for (const row of data.rows) {
      if (isIntegratorAdmin(row.roleId)) {
        continue;
      }
      next.set(row.roleId, moduleAccessMapFromRow(row));
    }
    setDraft(next);
  }, [data?.rows]);

  const rolesWithNoModuleAccess = useMemo(() => {
    if (!draft) {
      return [];
    }
    return tableRows.filter((row) => {
      const map = draft.get(row.roleId);
      return map == null || map.size === 0;
    });
  }, [draft, tableRows]);

  /**
   * Modules fully removed in the draft compared to last saved — each maps to a deep-link URL subtree
   * (`path` from the module definition).
   */
  const pendingModuleRemovalsByRole = useMemo(() => {
    if (!data?.rows || !draft) {
      return [];
    }
    const list: {
      roleId: number;
      displayName: string;
      removed: { key: string; label: string; path: string }[];
    }[] = [];
    for (const row of data.rows) {
      if (isIntegratorAdmin(row.roleId)) {
        continue;
      }
      const saved = moduleAccessMapFromRow(row);
      const current = draft.get(row.roleId) ?? new Map<string, HubModuleAccessLevel>();
      const removedKeys = [...saved.keys()].filter((key) => !current.has(key));
      if (removedKeys.length === 0) {
        continue;
      }
      const removed = removedKeys
        .map((key) => {
          const app = APPS.find((entry) => entry.key === key);
          return {
            key,
            label: app?.label ?? key,
            path: app?.path ?? `/${key}`,
          };
        })
        .sort((first, second) =>
          first.label.localeCompare(second.label, undefined, { sensitivity: "base" }),
        );
      list.push({ roleId: row.roleId, displayName: roleName(row), removed });
    }
    return list.sort((first, second) =>
      first.displayName.localeCompare(second.displayName, undefined, { sensitivity: "base" }),
    );
  }, [data?.rows, draft]);

  const dirty = useMemo(() => {
    if (!data?.rows || !draft) {
      return false;
    }
    for (const row of data.rows) {
      if (isIntegratorAdmin(row.roleId)) {
        continue;
      }
      const saved = moduleAccessMapFromRow(row);
      const current = draft.get(row.roleId) ?? new Map<string, HubModuleAccessLevel>();
      if (!moduleMapsEqual(saved, current)) {
        return true;
      }
    }
    return false;
  }, [data?.rows, draft]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft) {
        return Promise.reject(new Error("Nothing to save yet."));
      }
      const assignments = [...draft.entries()].map(([roleId, map]) => ({
        roleId,
        modules: [...map.entries()]
          .map(([key, access]) => ({ key, access }))
          .sort((first, second) => first.key.localeCompare(second.key)),
      }));
      return apiFetch<{ ok: boolean }>("/api/user-admin/subapp-assignments", {
        method: "PUT",
        body: { assignments },
      });
    },
    onSuccess: async () => {
      const userId = me?.user.id;
      if (userId) {
        await queryClient.invalidateQueries({ queryKey: meQueryKey(userId) });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      }
      await queryClient.invalidateQueries({ queryKey: ["user-admin", "subapp-assignments"] });
    },
  });

  const saveErrorMessage =
    saveMutation.isError
      ? saveMutation.error instanceof Error
        ? saveMutation.error.message
        : "Save failed. Try again."
      : null;
  const saveSucceeded = saveMutation.isSuccess && !dirty;

  const sortedModuleRowsForRolePivot = useMemo(() => {
    if (selectedRoleId === "") {
      return moduleRows;
    }
    const map = draft?.get(selectedRoleId) ?? new Map<string, HubModuleAccessLevel>();
    return [...moduleRows].sort((left, right) => {
      if (rolePivotSortKey === "module") {
        return compareStrings(left.label, right.label, rolePivotSortDir);
      }
      if (rolePivotSortKey === "path") {
        return compareStrings(left.path, right.path, rolePivotSortDir);
      }
      const leftAccess: "off" | HubModuleAccessLevel = map.get(left.key) ?? "off";
      const rightAccess: "off" | HubModuleAccessLevel = map.get(right.key) ?? "off";
      const rankDelta = accessSelectValueRank(leftAccess) - accessSelectValueRank(rightAccess);
      if (rankDelta !== 0) {
        return rolePivotSortDir === "asc" ? rankDelta : -rankDelta;
      }
      return compareStrings(left.label, right.label, "asc");
    });
  }, [draft, moduleRows, rolePivotSortDir, rolePivotSortKey, selectedRoleId]);

  const sortedRoleRowsForModulePivot = useMemo(() => {
    const mod = moduleRows.find((entry) => entry.key === selectedModuleKey);
    if (!mod || !draft) {
      return tableRows;
    }
    return [...tableRows].sort((left, right) => {
      if (modulePivotSortKey === "role") {
        return compareStrings(roleName(left), roleName(right), modulePivotSortDir);
      }
      const leftMap = draft.get(left.roleId) ?? new Map<string, HubModuleAccessLevel>();
      const rightMap = draft.get(right.roleId) ?? new Map<string, HubModuleAccessLevel>();
      const leftAccess: "off" | HubModuleAccessLevel = leftMap.get(mod.key) ?? "off";
      const rightAccess: "off" | HubModuleAccessLevel = rightMap.get(mod.key) ?? "off";
      const rankDelta = accessSelectValueRank(leftAccess) - accessSelectValueRank(rightAccess);
      if (rankDelta !== 0) {
        return modulePivotSortDir === "asc" ? rankDelta : -rankDelta;
      }
      return compareStrings(roleName(left), roleName(right), "asc");
    });
  }, [draft, modulePivotSortDir, modulePivotSortKey, moduleRows, selectedModuleKey, tableRows]);

  const toggleRolePivotSort = useCallback((key: RolePivotSortKey) => {
    if (rolePivotSortKey === key) {
      setRolePivotSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setRolePivotSortKey(key);
    setRolePivotSortDir(key === "access" ? "desc" : "asc");
  }, [rolePivotSortKey]);

  const toggleModulePivotSort = useCallback((key: ModulePivotSortKey) => {
    if (modulePivotSortKey === key) {
      setModulePivotSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setModulePivotSortKey(key);
    setModulePivotSortDir(key === "access" ? "desc" : "asc");
  }, [modulePivotSortKey]);

  const setModuleAccess = useCallback((roleId: number, moduleKey: string, value: "off" | HubModuleAccessLevel) => {
    setDraft((previous) => {
      if (!previous) {
        return previous;
      }
      const next = new Map(previous);
      const inner = new Map(next.get(roleId) ?? new Map<string, HubModuleAccessLevel>());
      if (value === "off") {
        inner.delete(moduleKey);
      } else {
        inner.set(moduleKey, value);
      }
      next.set(roleId, inner);
      return next;
    });
  }, []);

  if (!isIntegratorAdmin(me?.user)) {
    return <Navigate to="/admin" replace />;
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageTitleWithHelp hoverHint={modulePermissionsPageHint(1)}>
          Module permissions
        </PageTitleWithHelp>
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <PageTitleWithHelp hoverHint={modulePermissionsPageHint(1)}>
          Module permissions
        </PageTitleWithHelp>
        <p className="text-sm text-rose-600">
          {error instanceof Error ? error.message : "Could not load assignments."}
        </p>
        <Button type="button" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!data || !draft) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={modulePermissionsPageHint(data.scopeOrgId)}>
          Module permissions
        </PageTitleWithHelp>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <DeferredCommitStatusGlyph
            pending={saveMutation.isPending}
            isDirty={dirty}
            disabled={!hubWritesAllowed}
            commitError={saveErrorMessage}
            commitSucceeded={saveSucceeded}
            showSavedWhenIdle={true}
            savedHint="All changes saved"
            unsavedHint="Unsaved permission changes"
          />
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="inline">
            <HubSaveButton
              type="button"
              dirty={dirty}
              disabled={!hubWritesAllowed}
              blockedHint={adminReadOnlyTitle}
              isLoading={saveMutation.isPending}
              onClick={() => {
                saveMutation.reset();
                void saveMutation.mutate();
              }}
            >
              Save changes
            </HubSaveButton>
          </ReadOnlyHoverLayer>
        </div>
      </div>

      {rolesWithNoModuleAccess.length > 0 ? (
        <div
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/90 p-3 text-sm text-rose-950"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-700" aria-hidden />
          <div>
            <p className="font-medium text-rose-950">No module access for these roles</p>
            <p className="mt-1">
              {rolesWithNoModuleAccess.map((row) => roleName(row)).join(", ")} — every module is set to Off. Those
              accounts cannot use the hub until at least one module is enabled (they see a no-access message instead
              of a normal home page). Every deep link root is disabled for them until then.
            </p>
          </div>
        </div>
      ) : null}

      {pendingModuleRemovalsByRole.length > 0 ? (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/90 p-3 text-sm text-amber-950"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
          <div>
            <p className="font-medium text-amber-950">Disabling deep-link targets (not saved yet)</p>
            <p className="mt-1">
              Saving will cut off connectivity for the URL subtrees below for the listed roles (bookmarks, shared
              links, and any tool that opens the hub below that path).
            </p>
            <ul className="mt-2 list-inside list-disc space-y-2">
              {pendingModuleRemovalsByRole.map((entry) => (
                <li key={entry.roleId}>
                  <span className="font-medium">{entry.displayName}</span>
                  <ul className="mt-1 list-inside list-[circle] space-y-1 pl-4 text-slate-800">
                    {entry.removed.map((mod) => (
                      <li key={mod.key}>
                        <span className="font-medium text-amber-950">{mod.label}</span>
                        {" — "}paths starting with{" "}
                        <span className="font-mono text-xs text-amber-950">{mod.path}</span>
                        <span className="text-slate-700"> (nested routes included)</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <HubViewportCard chrome="extendedHeader">
        <CardHeader className="shrink-0">
          <div className="flex flex-col gap-4">
            <CardTitle>Permissions</CardTitle>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">View</span>
                <Button
                  type="button"
                  size="sm"
                  variant={pivotView === "role" ? "primary" : "secondary"}
                  onClick={() => setPivotView("role")}
                >
                  By role
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={pivotView === "module" ? "primary" : "secondary"}
                  onClick={() => setPivotView("module")}
                >
                  By module
                </Button>
              </div>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                <div className="flex flex-wrap items-end gap-2">
                  {showAddRole ? (
                    <form
                      className="flex flex-wrap items-end gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        submitNewRole();
                      }}
                    >
                      <div className="min-w-[12rem]">
                        <label htmlFor="new-role-name" className="text-xs font-medium text-slate-600">
                          New role name
                        </label>
                        <input
                          id="new-role-name"
                          type="text"
                          value={newRoleName}
                          disabled={!hubWritesAllowed || addRoleMutation.isPending}
                          onChange={(event) => setNewRoleName(event.target.value)}
                          className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm"
                          placeholder="For example Manufacturing lead"
                        />
                      </div>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!hubWritesAllowed || !newRoleName.trim() || addRoleMutation.isPending}
                      >
                        {addRoleMutation.isPending ? "Adding…" : "Add role"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowAddRole(false);
                          setNewRoleName("");
                        }}
                      >
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!hubWritesAllowed}
                      onClick={() => setShowAddRole(true)}
                    >
                      Add role
                    </Button>
                  )}
                  {addRoleMutation.isError ? (
                    <p className="text-sm text-rose-600" role="alert">
                      {addRoleMutation.error instanceof Error
                        ? addRoleMutation.error.message
                        : "Could not add role."}
                    </p>
                  ) : null}
                  {addRoleMutation.isSuccess ? (
                    <p className="text-sm text-emerald-700">
                      Added role {addRoleMutation.data.role.name ?? `id ${addRoleMutation.data.role.id}`}.
                    </p>
                  ) : null}
                </div>
              </ReadOnlyHoverLayer>
            </div>
            {pivotView === "role" ? (
              <div className="max-w-md">
                <label htmlFor="permissions-role-picker" className="text-sm font-medium text-slate-700">
                  Role
                </label>
                <select
                  id="permissions-role-picker"
                  value={selectedRoleId === "" ? "" : String(selectedRoleId)}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedRoleId(value === "" ? "" : Number.parseInt(value, 10));
                  }}
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                >
                  {tableRows.map((row) => (
                    <option key={row.roleId} value={row.roleId}>
                      {roleName(row)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="max-w-md">
                <label htmlFor="permissions-module-picker" className="text-sm font-medium text-slate-700">
                  Module
                </label>
                <select
                  id="permissions-module-picker"
                  value={selectedModuleKey}
                  onChange={(event) => setSelectedModuleKey(event.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                >
                  {moduleRows.map((mod) => (
                    <option key={mod.key} value={mod.key}>
                      {mod.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody className={cn(hubTableScrollRegionClassName(), "p-0")}>
          {tableRows.length === 0 ? (
            <p className="p-4 text-sm text-slate-600">No non-admin roles were returned from the server.</p>
          ) : pivotView === "role" && selectedRoleId !== "" ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead className={hubStickyTableHeadClassName("slate")}>
                  <tr>
                    <SortableTableHeader
                      label="Module"
                      sortKey="module"
                      activeKey={rolePivotSortKey}
                      dir={rolePivotSortDir}
                      onSort={toggleRolePivotSort}
                      className="py-2 pr-4 normal-case tracking-normal"
                    />
                    <SortableTableHeader
                      label="Deep link root"
                      sortKey="path"
                      activeKey={rolePivotSortKey}
                      dir={rolePivotSortDir}
                      onSort={toggleRolePivotSort}
                      className="py-2 pr-4 normal-case tracking-normal"
                    />
                    <SortableTableHeader
                      label="Access"
                      sortKey="access"
                      activeKey={rolePivotSortKey}
                      dir={rolePivotSortDir}
                      onSort={toggleRolePivotSort}
                      className="py-2 normal-case tracking-normal"
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedModuleRowsForRolePivot.map((mod) => {
                    const map = draft.get(selectedRoleId) ?? new Map<string, HubModuleAccessLevel>();
                    const level = map.get(mod.key);
                    const selectValue: "off" | HubModuleAccessLevel = level ?? "off";
                    const selectedRow = tableRows.find((row) => row.roleId === selectedRoleId);
                    const roleLabel = selectedRow ? roleName(selectedRow) : `Role ${selectedRoleId}`;
                    return (
                      <tr key={mod.key} className="border-b border-slate-100 last:border-0">
                        <td className="px-4 py-2 pr-4 align-top text-slate-900">
                          <HoverHintContent hoverHint={mod.description}>
                            <span className="font-medium">{mod.label}</span>
                          </HoverHintContent>
                        </td>
                        <td className="px-4 py-2 pr-4 align-top font-mono text-xs text-slate-600">{mod.path}</td>
                        <td className="px-4 py-2 align-top">
                          <label className="sr-only" htmlFor={`access-${selectedRoleId}-${mod.key}`}>
                            {mod.label} access for {roleLabel}
                          </label>
                          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                            <select
                              id={`access-${selectedRoleId}-${mod.key}`}
                              disabled={!hubWritesAllowed}
                              className={ACCESS_SELECT_CLASS}
                              value={selectValue}
                              onChange={(event) => {
                                const next = event.target.value as "off" | HubModuleAccessLevel;
                                setModuleAccess(selectedRoleId, mod.key, next);
                              }}
                            >
                              <option value="off">Off</option>
                              <option value="read">Read only</option>
                              <option value="read_write">Read + write</option>
                              <option value="admin">Module administrator</option>
                            </select>
                          </ReadOnlyHoverLayer>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : pivotView === "module" && selectedModuleKey ? (
            <div className="overflow-x-auto">
              {(() => {
                const mod = moduleRows.find((entry) => entry.key === selectedModuleKey);
                if (!mod) {
                  return <p className="p-4 text-sm text-slate-600">Select a module.</p>;
                }
                return (
                  <>
                    <p className="border-b border-slate-100 px-4 py-3 text-sm text-slate-600">
                      <HoverHintContent hoverHint={mod.description}>
                        <span className="font-medium text-slate-900">{mod.label}</span>
                      </HoverHintContent>
                      {" — "}deep link root{" "}
                      <span className="font-mono text-xs">{mod.path}</span>
                    </p>
                    <table className="w-full border-collapse text-left text-sm">
                      <thead className={hubStickyTableHeadClassName("slate")}>
                        <tr>
                          <SortableTableHeader
                            label="Role"
                            sortKey="role"
                            activeKey={modulePivotSortKey}
                            dir={modulePivotSortDir}
                            onSort={toggleModulePivotSort}
                            className="py-2 pr-4 normal-case tracking-normal"
                          />
                          <SortableTableHeader
                            label="Access"
                            sortKey="access"
                            activeKey={modulePivotSortKey}
                            dir={modulePivotSortDir}
                            onSort={toggleModulePivotSort}
                            className="py-2 normal-case tracking-normal"
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRoleRowsForModulePivot.map((row) => {
                          const map = draft.get(row.roleId) ?? new Map<string, HubModuleAccessLevel>();
                          const level = map.get(mod.key);
                          const selectValue: "off" | HubModuleAccessLevel = level ?? "off";
                          return (
                            <tr key={row.roleId} className="border-b border-slate-100 last:border-0">
                              <td className="px-4 py-2 pr-4 font-medium text-slate-900">{roleName(row)}</td>
                              <td className="px-4 py-2">
                                <label className="sr-only" htmlFor={`access-${row.roleId}-${mod.key}`}>
                                  {mod.label} access for {roleName(row)}
                                </label>
                                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                                  <select
                                    id={`access-${row.roleId}-${mod.key}`}
                                    disabled={!hubWritesAllowed}
                                    className={ACCESS_SELECT_CLASS}
                                    value={selectValue}
                                    onChange={(event) => {
                                      const next = event.target.value as "off" | HubModuleAccessLevel;
                                      setModuleAccess(row.roleId, mod.key, next);
                                    }}
                                  >
                                    <option value="off">Off</option>
                                    <option value="read">Read only</option>
                                    <option value="read_write">Read + write</option>
                                    <option value="admin">Module administrator</option>
                                  </select>
                                </ReadOnlyHoverLayer>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>
          ) : null}
        </CardBody>
      </HubViewportCard>
    </div>
  );
}
